import { DetailSection } from "@/components/ui/detail-field";
import { useState } from "react";
import { Building2, Repeat, Shield } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useRolesAsignados } from "@/hooks/useCambiarRolActivo";
import { CambiarRolDialog } from "@/components/auth/CambiarRolDialog";
import { StatusPill } from "@/components/ui/status-pill";
import { Button } from "@/components/ui/button";
import { EmptyState, InlineLoading } from "@/components/ui/data-states";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { coincideVerComo } from "@/components/ui/selector-ver-como";
import type { ProyectosConAcceso } from "./useProyectosConAcceso";

/**
 * Roles y acceso: roles (principal y adicionales) y proyectos a los que entras. El cambio de rol
 * activo es el mismo flujo del header (`CambiarRolDialog` + `useActivarRol`).
 */
/** Proyectos a la vista antes de "+N más" (los de SOZU van primero: Monócolo, Daiku, Bottura, Margot). */
const VISIBLES = 5;

export function SeccionRoles({
  proyectos,
  vista,
}: {
  proyectos: ProyectosConAcceso;
  /** "Ver como": solo el rol con el que la persona vista entra; sin cambio de rol. */
  vista?: { rolNombre: string | null };
}) {
  const { profile } = useAuth();
  const { roles, elegiblesAdmin } = useRolesAsignados();
  const [cambiando, setCambiando] = useState(false);

  return (
    <>
      <DetailSection
        icon={Shield}
        title="Tus roles"
        description={vista ? "El rol con el que entra define los menús y los datos que ve." : "Tu rol activo define los menús y los datos que ves."}
        action={!vista && elegiblesAdmin.length > 1 && (
          <Button variant="primary-outline" size="compact" onClick={() => setCambiando(true)}>
            <Repeat /> Cambiar de rol
          </Button>
        )}
      >
        {vista ? (
          <ul className="space-y-1">
            <li className="flex flex-wrap items-center justify-between gap-2 rounded-md py-1.5">
              <p className="break-words text-[13px] font-medium text-foreground">{vista.rolNombre || "Sin rol"}</p>
              <StatusPill tone="success">Activo</StatusPill>
            </li>
          </ul>
        ) : roles === null ? (
          <InlineLoading label="Cargando roles..." />
        ) : roles.length === 0 ? (
          <p className="py-4 text-[13px] text-muted-foreground">No tienes roles asignados.</p>
        ) : (
          <ul className="space-y-1">
            {roles.map((r) => {
              const activo = r.rol_id === profile?.rol_id;
              const empresa = r.persona_contexto_nombre?.trim();
              return (
                <li key={`${r.rol_id}-${r.id_persona_contexto ?? ""}`} className="flex flex-wrap items-center justify-between gap-2 rounded-md py-1.5">
                  <div className="min-w-0">
                    <p className="break-words text-[13px] font-medium text-foreground">{r.nombre}</p>
                    {empresa && <p className="break-words text-[12px] text-muted-foreground">{empresa}</p>}
                  </div>
                  <div className="flex flex-wrap items-center gap-1.5">
                    {r.es_principal && <StatusPill tone="neutral" hideDot>Principal</StatusPill>}
                    {activo ? <StatusPill tone="success">Activo</StatusPill> : <StatusPill tone="neutral" hideDot>Adicional</StatusPill>}
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </DetailSection>

      <DetailSection
        icon={Building2}
        title={proyectos.cargando ? "Proyectos con acceso" : `Proyectos con acceso (${proyectos.lista.length.toLocaleString("es-MX")})`}
        description={proyectos.todos
          ? "El rol ve todos los proyectos activos, también los que se den de alta después."
          : vista ? "Solo ve la información de estos proyectos." : "Solo ves la información de estos proyectos."}
      >
        {proyectos.cargando ? (
          <InlineLoading label="Cargando proyectos..." />
        ) : proyectos.lista.length === 0 ? (
          <EmptyState compacto icono={Building2} titulo="Sin proyectos asignados" />
        ) : (
          <div className="flex flex-wrap items-center gap-1.5">
            {proyectos.lista.slice(0, VISIBLES).map((p) => (
              <StatusPill key={p.id} tone={p.sozu ? "success" : "neutral"} hideDot>{p.nombre}</StatusPill>
            ))}
            {proyectos.lista.length > VISIBLES && (
              // Con cientos de proyectos no se pintan todos: "+N más" abre la lista con buscador.
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="compact" className="h-6 px-2 text-[12px] text-primary">
                    +{(proyectos.lista.length - VISIBLES).toLocaleString("es-MX")} más
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 p-0">
                  <Command filter={coincideVerComo}>
                    <CommandInput placeholder="Buscar proyecto..." />
                    <CommandList className="max-h-64">
                      <CommandEmpty>Sin proyectos con ese nombre.</CommandEmpty>
                      {proyectos.lista.map((p) => (
                        <CommandItem key={p.id} value={p.nombre} className="flex items-center justify-between gap-2">
                          <span className="min-w-0 truncate">{p.nombre}</span>
                          {p.sozu && <StatusPill tone="success" hideDot>SOZU</StatusPill>}
                        </CommandItem>
                      ))}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </div>
        )}
      </DetailSection>

      {!vista && elegiblesAdmin.length > 1 && <CambiarRolDialog open={cambiando} onOpenChange={setCambiando} />}
    </>
  );
}
